import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, X, Terminal, StickyNote, FolderGit2, User, Globe, 
  Music, Mail, Trash2, Monitor, FileText, Command, ArrowRight,
  ExternalLink, Clock, Sparkles, Timer
} from 'lucide-react';
import { useOS, AppId } from '@/lib/os-context';
import { cn } from '@/lib/utils';
import { getGlassStyle, isGlassSupported } from '@/lib/glass-theme';
import { liquidPanel } from '@/lib/liquid-motion';
import { highlightText } from '@/components/ui/search-input';

interface SearchItem {
  id: string;
  type: 'app' | 'action' | 'link' | 'recent';
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
}

interface SpotlightSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SpotlightSearch({ isOpen, onClose }: SpotlightSearchProps) {
  const { openWindow, displaySettings, effectiveColorMode } = useOS();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Glass theme support
  const isTahoe = displaySettings.theme === 'tahoe';
  const glassEnabled = isTahoe && isGlassSupported();
  const glassStyles = glassEnabled 
    ? getGlassStyle(displaySettings.glassIntensity, displaySettings.glassVariant, effectiveColorMode)
    : {};

  // Define searchable items
  const allItems: SearchItem[] = useMemo(() => [
    // Apps
    {
      id: 'profile',
      type: 'app',
      title: 'Profile',
      subtitle: 'View my profile and about me',
      icon: <User className="w-5 h-5 text-blue-400" />,
      action: () => { openWindow('profile'); onClose(); },
      keywords: ['about', 'me', 'bio', 'resume', 'cv'],
    },
    {
      id: 'notes',
      type: 'app',
      title: 'Notes',
      subtitle: 'Markdown notes and documentation',
      icon: <StickyNote className="w-5 h-5 text-yellow-400" />,
      action: () => { openWindow('notes'); onClose(); },
      keywords: ['markdown', 'docs', 'write', 'text'],
    },
    {
      id: 'terminal',
      type: 'app',
      title: 'Terminal',
      subtitle: 'Interactive command line',
      icon: <Terminal className="w-5 h-5 text-green-400" />,
      action: () => { openWindow('terminal'); onClose(); },
      keywords: ['console', 'cli', 'command', 'shell', 'bash'],
    },
    {
      id: 'projects',
      type: 'app',
      title: 'Projects',
      subtitle: 'Portfolio and project showcase',
      icon: <FolderGit2 className="w-5 h-5 text-blue-400" />,
      action: () => { openWindow('projects'); onClose(); },
      keywords: ['portfolio', 'work', 'github', 'code'],
    },
    {
      id: 'browser',
      type: 'app',
      title: 'Browser',
      subtitle: 'Social links and bookmarks',
      icon: <Globe className="w-5 h-5 text-blue-400" />,
      action: () => { openWindow('browser'); onClose(); },
      keywords: ['web', 'social', 'links', 'internet'],
    },
    {
      id: 'music',
      type: 'app',
      title: 'Music',
      subtitle: 'Music player widget',
      icon: <Music className="w-5 h-5 text-green-400" />,
      action: () => { openWindow('music'); onClose(); },
      keywords: ['spotify', 'player', 'songs', 'playlist'],
    },
    {
      id: 'focus',
      type: 'app',
      title: 'Focus Studio',
      subtitle: 'Pomodoro, tasks, lofi, and ambience mixer',
      icon: <Timer className="w-5 h-5 text-blue-400" />,
      action: () => { openWindow('focus'); onClose(); },
      keywords: ['pomodoro', 'todo', 'tasks', 'lofi', 'rain', 'focus', 'study'],
    },
    {
      id: 'contact',
      type: 'app',
      title: 'Contact',
      subtitle: 'Get in touch with me',
      icon: <Mail className="w-5 h-5 text-orange-400" />,
      action: () => { openWindow('contact'); onClose(); },
      keywords: ['email', 'message', 'hire', 'reach'],
    },
    {
      id: 'display',
      type: 'app',
      title: 'Display Settings',
      subtitle: 'Theme, wallpaper, and appearance',
      icon: <Monitor className="w-5 h-5 text-gray-400" />,
      action: () => { openWindow('display'); onClose(); },
      keywords: ['theme', 'dark', 'light', 'wallpaper', 'settings'],
    },
    {
      id: 'trash',
      type: 'app',
      title: 'Trash',
      subtitle: 'Deleted items',
      icon: <Trash2 className="w-5 h-5 text-gray-400" />,
      action: () => { openWindow('trash'); onClose(); },
      keywords: ['delete', 'bin', 'recycle'],
    },
    // Quick Actions
    {
      id: 'action-github',
      type: 'link',
      title: 'Open GitHub',
      subtitle: 'github.com/CodeVectorizer',
      icon: <ExternalLink className="w-5 h-5 text-purple-400" />,
      action: () => { window.open('https://github.com/CodeVectorizer', '_blank'); onClose(); },
      keywords: ['github', 'code', 'repository'],
    },
    {
      id: 'action-linkedin',
      type: 'link',
      title: 'Open LinkedIn',
      subtitle: 'linkedin.com/in/lukmanafandi',
      icon: <ExternalLink className="w-5 h-5 text-blue-500" />,
      action: () => { window.open('https://linkedin.com', '_blank'); onClose(); },
      keywords: ['linkedin', 'job', 'career', 'network'],
    },
    {
      id: 'action-email',
      type: 'action',
      title: 'Send Email',
      subtitle: 'Open email composer',
      icon: <Mail className="w-5 h-5 text-red-400" />,
      action: () => { window.open('mailto:lukmanafandi10@gmail.com', '_blank'); onClose(); },
      keywords: ['email', 'mail', 'contact'],
    },
  ], [openWindow, onClose]);

  // Filter items based on query
  const filteredItems = useMemo(() => {
    if (!query.trim()) {
      // Show recent/suggested items when no query
      return allItems.slice(0, 6);
    }

    const lowerQuery = query.toLowerCase();
    return allItems.filter(item => {
      const matchesTitle = item.title.toLowerCase().includes(lowerQuery);
      const matchesSubtitle = item.subtitle?.toLowerCase().includes(lowerQuery);
      const matchesKeywords = item.keywords?.some(k => k.includes(lowerQuery));
      return matchesTitle || matchesSubtitle || matchesKeywords;
    });
  }, [query, allItems]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredItems]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredItems.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredItems[selectedIndex]) {
            filteredItems[selectedIndex].action();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredItems, selectedIndex, onClose]);

  // Close on click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getTypeLabel = (type: SearchItem['type']) => {
    switch (type) {
      case 'app': return 'Application';
      case 'action': return 'Quick Action';
      case 'link': return 'External Link';
      case 'recent': return 'Recent';
      default: return '';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-99999 flex items-start justify-center pt-[15vh]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <motion.div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Search Modal */}
          <motion.div
            className={cn(
              "relative w-full max-w-2xl mx-4 rounded-2xl shadow-2xl overflow-hidden",
              isTahoe && "tahoe-liquid-panel",
              displaySettings.glassVariant === 'clear' && isTahoe && "tahoe-clear-glass",
              isTahoe
                ? "border-white/45"
                : glassEnabled
                ? "border-white/15" 
                : "bg-card/95 backdrop-blur-xl border border-border"
            )}
            style={glassEnabled && !isTahoe ? {
              ...glassStyles,
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
            } : {}}
            variants={isTahoe ? liquidPanel : undefined}
            initial={isTahoe ? "hidden" : { opacity: 0, scale: 0.95, y: -20 }}
            animate={isTahoe ? "visible" : { opacity: 1, scale: 1, y: 0 }}
            exit={isTahoe ? "exit" : { opacity: 0, scale: 0.95, y: -20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Glass highlight */}
            {glassEnabled && (
              <div 
                className="absolute inset-0 rounded-2xl pointer-events-none z-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                }}
              />
            )}
            {/* Search Input */}
            <div className={cn(
              "flex items-center gap-3 px-5 py-4 border-b relative z-10",
              glassEnabled ? "border-white/10" : "border-border"
            )}>
              <Search className="w-5 h-5 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search apps, actions, or links..."
                className="flex-1 bg-transparent text-lg text-foreground placeholder:text-muted-foreground focus:outline-none"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="p-2 rounded-md hover:bg-secondary transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">ESC</kbd>
                <span>to close</span>
              </div>
            </div>

            {/* Results */}
            <div className="max-h-[400px] overflow-y-auto relative z-10">
              {filteredItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Sparkles className="w-12 h-12 mb-3 opacity-50" />
                  <p className="text-sm">No results found for "{query}"</p>
                  <p className="text-xs mt-1">Try a different search term</p>
                </div>
              ) : (
                <div className="py-2">
                  {/* Group by type */}
                  {!query && (
                    <div className="px-4 py-2">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Suggestions
                      </span>
                    </div>
                  )}
                  
                  {filteredItems.map((item, index) => (
                    <motion.button
                      key={item.id}
                      className={cn(
                        "w-full flex items-center gap-4 px-5 py-3 text-left transition-colors",
                        selectedIndex === index 
                          ? "bg-primary/10 text-foreground" 
                          : "hover:bg-secondary/50 text-foreground"
                      )}
                      onClick={item.action}
                      onMouseEnter={() => setSelectedIndex(index)}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      {/* Icon */}
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center",
                        selectedIndex === index ? "bg-primary/20" : "bg-secondary"
                      )}>
                        {item.icon}
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{highlightText(item.title, query)}</span>
                          <span className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                            {getTypeLabel(item.type)}
                          </span>
                        </div>
                        {item.subtitle && (
                          <p className="text-sm text-muted-foreground truncate">
                            {highlightText(item.subtitle, query)}
                          </p>
                        )}
                      </div>

                      {/* Arrow indicator for selected */}
                      {selectedIndex === index && (
                        <ArrowRight className="w-4 h-4 text-primary" />
                      )}
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={cn(
              "flex items-center justify-between px-5 py-3 border-t relative z-10",
              glassEnabled 
                ? "border-white/10 bg-white/5" 
                : "border-border bg-secondary/30"
            )}>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">↓</kbd>
                  <span>navigate</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">↵</kbd>
                  <span>open</span>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Command className="w-3 h-3" />
                <span>+ K to open</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Global keyboard shortcut hook
export function useSpotlightShortcut(onOpen: () => void) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onOpen]);
}
